import { compactFormat } from "~/assets/js/compactForm";
import colors from "~/assets/theme/colors";

const CIRCLE_STYLES = [
  "bg-status-warning text-back-lightest",
  "bg-primary-dark text-back-lightest",
  "bg-primary text-back-lightest",
  "bg-primary-light text-back-lightest",
];

const DOUGHNUT_COLORS = [
  colors.primary.DEFAULT,
  colors.primary.dark,
  colors.primary.darkest,
  colors.primary.light,
  colors.primary.lightest,
];

export const toCircle = ({ index, title, title2, subtitle, value }) => ({
  title,
  subtitle,
  title2,
  value,
  styles: CIRCLE_STYLES[index % CIRCLE_STYLES.length],
});

export const toDoughnutItem = (item, index, measure, wilcardKey, wilcardKeyTranslation) => {
  let name = "";
  if (item.name === wilcardKey) {
    name = wilcardKeyTranslation;
  } else {
    name = item.name || "";
  }

  return {
    name,
    measure,
    value: item.value || item.days || 0,
    color: DOUGHNUT_COLORS[index % DOUGHNUT_COLORS.length],
  };
};

export const toDoughnutGroup = (
  items,
  { title, measure, emptyMessage, alert = true, wilcardKey, wilcardKeyTranslation },
) => ({
  title,
  emptyMessage,
  alert,
  elements: items.map((item, index) =>
    toDoughnutItem(item, index, measure, wilcardKey, wilcardKeyTranslation),
  ),
});

export const toPyramidItem = (item) => ({
  name: item.name || "",
  value: item.value || 0,
});

export const toPyramidGroup = (items, title, emptyMessage, alert) => ({
  title,
  emptyMessage,
  alert,
  elements: items.map((item) => toPyramidItem(item)),
});

export const toNumberWidgetData = (circle, { icon, label }) => ({
  value: compactFormat(circle.value),
  icon,
  detail: { label },
});

export const toPieSeries = (doughnutGroup) =>
  doughnutGroup.elements.map((element, index) => ({
    id: `${index}`,
    label: element.name,
    value: element.value,
    color: element.color,
  }));

export const toBarData = (pyramidGroup) => ({
  labels: pyramidGroup.elements.map((element) => element.name),
  values: pyramidGroup.elements.map((element) => element.value),
});

const toEvolution = (stats, field) => ({
  labels: stats.map((stat) => stat.updatedOn),
  datasets: [
    {
      label: "",
      data: stats.map((stat) => stat[field]),
    },
  ],
});

export const toComputerEvolution = (stats) => toEvolution(stats, "agents");

export const toVaccineEvolution = (stats) => toEvolution(stats, "vaccines");

export const toAlertEvolution = (stats) => toEvolution(stats, "alerts");

export const toAgencyEvolution = (stats) => toEvolution(stats, "agencies");

export const toAlertsPerDayEvolution = (stats) => toEvolution(stats, "alertsPerDay");

export const toAgentsPerDayEvolution = (stats) => toEvolution(stats, "agentsPerDay");

export const toAlertsPerVaccineEvolution = (stats) => {
  const datasetsMap = new Map();
  for (const stat of stats) {
    for (const vaccineAlert of stat.vaccineAlerts) {
      if (!datasetsMap.has(vaccineAlert.vaccine)) {
        datasetsMap.set(vaccineAlert.vaccine, vaccineAlert.alerts);
      } else {
        const value = [datasetsMap.get(vaccineAlert.vaccine), vaccineAlert.alerts].flat();
        datasetsMap.set(vaccineAlert.vaccine, value);
      }
    }
  }

  return {
    labels: stats.map((stat) => stat.updatedOn),
    datasets: Array.from(datasetsMap.entries()).map(([label, data]) => ({ label, data })),
  };
};

export const toAgentsPerVersionEvolution = (stats) => {
  const datasetsMap = new Map();
  for (const stat of stats) {
    for (const versionAgent of stat.versionAgents) {
      if (!datasetsMap.has(versionAgent.version)) {
        datasetsMap.set(versionAgent.version, [versionAgent.agents]);
      } else {
        const value = [datasetsMap.get(versionAgent.version), versionAgent.agents].flat();
        datasetsMap.set(versionAgent.version, value);
      }
    }
  }

  return {
    labels: stats.map((stat) => stat.updatedOn),
    datasets: Array.from(datasetsMap.entries()).map(([label, data]) => ({ label, data })),
  };
};
